import {Bindable} from './Bindable';
import util from './Util';

/**
 * Represent a mouse event.
 *
 * @class Mouse.MouseEvent
 * @param {Mouse.Event} type Either mousedown/mouseup/mousemove
 * @param {Number} button Button id
 * @param {Number} offsetX Relative to target element
 * @param {Number} offsetY Relative to target element
 * @param {Number} clientX Relative to the upper left edge of the browser window
 * @param {Number} clientY Relative to the upper left edge of the browser window
 * @param {Number} screenX Relative to the top left of the physical screen/monitor
 * @param {Number} screenY Relative to the top left of the physical screen/monitor
 * @param {Number} pageX Relative the to the top left of the fully rendered content area in the browser
 * @param {Number} pageY Relative the to the top left of the fully rendered content area in the browser
 * @param {Object} target Target element
 * @param {Object} original Original event if available
 * @constructor
 * @module Core
 */
export class MouseEvent {
    constructor(public type,
                public button,
                public offsetX, public offsetY,
                public clientX, public clientY,
                public screenX, public screenY,
                public pageX, public pageY,
                public target,
                public original) {
    }
}

/**
 * Provides mouse events.
 *
 * Can fire the following events:
 *
 *    > MOUSEDOWN - fired on mouse down
 *        info - Mouse.MouseEvent info
 *    > MOUSEUP - fired on mouse up
 *        info - Mouse.MouseEvent info
 *    > MOUSEMOVE - fired on mouse move
 *        info - Mouse.MouseEvent info
 *
 * @class Mouse
 * @extends Bindable
 * @constructor
 * @module Core
 */
export class Mouse extends Bindable {
    private _buttonDown = [];

    /**
     * Event types.
     *
     * @event
     * @param {Object} Event
     * @param {String} Event.MOUSEDOWN Triggered on mouse down
     * @param {String} Event.MOUSEUP Triggered on mouse up
     * @param {String} Event.MOUSEMOVE Triggered on mouse move
     */
    Event = {
        MOUSEDOWN: 'mousedown',
        MOUSEUP: 'mouseup',
        MOUSEMOVE: 'mousemove'
    };

    /**
     * Represents mouse buttons.
     *
     * @property Button
     * @type {Object}
     */
    Button = {
        LEFT: 0,
        MIDDLE: 1,
        RIGHT: 2
    };


    /**
     * Initiates the components.
     *
     * @method init
     * @return {Mouse} Self
     */
    init() {
        $(document.body).bind('mousedown mouseup mousemove', (e) => {
            this.consume(this.generate(
                e.type,
                e.button,
                e.offsetX, e.offsetY,
                e.clientX, e.clientY,
                e.screenX, e.screenY,
                e.pageX, e.pageY,
                e.target,
                e.originalEvent
            ));
        });

        return this;
    };

    /**
     * Represent a mouse event.
     *
     * @method generate
     * @param {String} type Either mousedown/mouseup/mousemove
     * @param {Number} button Button id
     * @param {Number} offsetX Relative to target element
     * @param {Number} offsetY Relative to target element
     * @param {Number} clientX Relative to the upper left edge of the browser window
     * @param {Number} clientY Relative to the upper left edge of the browser window
     * @param {Number} screenX Relative to the top left of the physical screen/monitor
     * @param {Number} screenY Relative to the top left of the physical screen/monitor
     * @param {Number} pageX Relative the to the top left of the fully rendered content area in the browser
     * @param {Number} pageY Relative the to the top left of the fully rendered content area in the browser
     * @param {Object} [target] Target element
     * @param {Object} [original] Original event if available
     * @constructor
     * @module Core
     */
    generate(type,
             button,
             offsetX, offsetY,
             clientX, clientY,
             screenX, screenY,
             pageX, pageY,
             target,
             original) {
        button = util.isNumber(button) ? button : 0;
        clientX = util.isNumber(clientX) ? clientX : offsetX;
        clientY = util.isNumber(clientY) ? clientY : offsetY;
        screenX = util.isNumber(screenX) ? screenX : offsetX;
        screenY = util.isNumber(screenY) ? screenY : offsetY;
        pageX = util.isNumber(pageX) ? pageX : offsetX;
        pageY = util.isNumber(pageY) ? pageY : offsetY;
        original = !util.isUndefined(original) ? original : null;
        target = !util.isUndefined(target) ? target : null;

        return new MouseEvent(
            type,
            button,
            offsetX, offsetY,
            clientX, clientY,
            screenX, screenY,
            pageX, pageY,
            target,
            original
        );
    }

    /**
     * Consumes mouse event, passing it on to all listeners.
     *
     * @method consume
     * @param {Mouse.MouseEvent} event Event to consume
     */
    consume(event) {
        if (event.type === this.Event.MOUSEDOWN) {
            if (!util.contains(this._buttonDown, event.button)) {
                this._buttonDown.push(event.button);
            }
        } else if (event.type === this.Event.MOUSEUP) {
            if (util.contains(this._buttonDown, event.button)) {
                util.remove(event.button, this._buttonDown);
            }
        } else if (event.type !== this.Event.MOUSEMOVE) {
            throw new Error('Unexpected mouse event type "' + event.type + '"');
        }

        this.fire({
            type: event.type,
            info: event
        });
    }

    /**
     * Returns whether given button is currently down.
     *
     * @method isButtonDown
     * @param {Number} button Button id
     * @return {Boolean}
     */
    isButtonDown(button) {
        return util.contains(this._buttonDown, button);
    }

    static MouseEvent = MouseEvent;
}


export default new Mouse();


// WEBPACK FOOTER //
// ./src/main/ui/core/application/Mouse.ts